import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { IAddPurchaseItemResultRequest } from '../../../../../model/scp/purchase-item-result/request/add-purchase-item-result-request';
import { IUpdatePurchaseItemResultRequest } from '../../../../../model/scp/purchase-item-result/request/update-purchase-item-result-request';
import { IPurchaseItemResultResponse } from '../../../../../model/scp/purchase-item-result/response/purchase-item-result-response';
import { ITableResponse } from '../../../../../model/scp/purchase/response/tables-response';

export interface IPurchaseItemResultState {
    resultRequest: IAddPurchaseItemResultRequest | IUpdatePurchaseItemResultRequest;
    isEditMode: boolean;
    legalNatures: ITableResponse[];
    isOpen: boolean;
    purchaseId: number;
    itemNumber: number;
    resultData?: IPurchaseItemResultResponse | null;
}

type Action =
    | { type: 'SET_RESULT_REQUEST'; payload: Partial<IAddPurchaseItemResultRequest | IUpdatePurchaseItemResultRequest> }
    | { type: 'SET_IS_EDIT_MODE'; payload: boolean }
    | { type: 'SET_LEGAL_NATURES'; payload: ITableResponse[] }
    | { type: 'SET_IS_OPEN'; payload: boolean }
    | { type: 'SET_PURCHASE_ID'; payload: number }
    | { type: 'SET_ITEM_NUMBER'; payload: number }
    | { type: 'SET_RESULT_DATA'; payload: IPurchaseItemResultResponse | null }
    | { type: 'RESET_STATE' };

export const ACTION_TYPES = {
    setResultRequest: 'SET_RESULT_REQUEST',
    setIsEditMode: 'SET_IS_EDIT_MODE',
    setLegalNatures: 'SET_LEGAL_NATURES',
    setIsOpen: 'SET_IS_OPEN',
    setPurchaseId: 'SET_PURCHASE_ID',
    setItemNumber: 'SET_ITEM_NUMBER',
    setResultData: 'SET_RESULT_DATA',
    resetState: 'RESET_STATE'
} as const;

const getInitialResultRequest = (): IAddPurchaseItemResultRequest | IUpdatePurchaseItemResultRequest => ({
    approvedQuantity: 0,
    approvedUnitPrice: 0,
    approvedTotalPrice: 0,
    discountPercentage: 0,
    personTypeId: '',
    supplierNi: '',
    supplierCompanyName: '',
    supplierSizeId: 0,
    countryCode: '',
    subcontractingIndicator: false,
    meEppBenefitApplication: false,
    srpClassificationOrder: 0,
    resultDate: new Date(),
    legalNatureId: '',
    descriptionBrand: '',
    itemResultStatusId: 0
});

const initialState: IPurchaseItemResultState = {
    resultRequest: getInitialResultRequest(),
    isEditMode: false,
    legalNatures: [],
    isOpen: false,
    purchaseId: 0,
    itemNumber: 0,
    resultData: null
};

function purchaseItemResultReducer(state: IPurchaseItemResultState, action: Action): IPurchaseItemResultState {
    switch (action.type) {
        case 'SET_RESULT_REQUEST':
            return {
                ...state,
                resultRequest: {
                    ...state.resultRequest,
                    ...action.payload
                } as IAddPurchaseItemResultRequest | IUpdatePurchaseItemResultRequest
            };
        case 'SET_IS_EDIT_MODE':
            return { ...state, isEditMode: action.payload };
        case 'SET_LEGAL_NATURES':
            return { ...state, legalNatures: action.payload };
        case 'SET_IS_OPEN':
            return { ...state, isOpen: action.payload };
        case 'SET_PURCHASE_ID':
            return { ...state, purchaseId: action.payload };
        case 'SET_ITEM_NUMBER':
            return { ...state, itemNumber: action.payload };
        case 'SET_RESULT_DATA':
            return { ...state, resultData: action.payload };
        case 'RESET_STATE':
            return {
                ...state,
                resultRequest: getInitialResultRequest(),
                isEditMode: false
            };
        default:
            return state;
    }
}

const PurchaseItemResultStateContext = createContext<IPurchaseItemResultState | undefined>(undefined);
const PurchaseItemResultDispatchContext = createContext<React.Dispatch<Action> | undefined>(undefined);

interface PurchaseItemResultProviderProps {
    children: ReactNode;
    initialIsOpen?: boolean;
    initialPurchaseId?: number;
    initialItemNumber?: number;
    initialResultData?: IPurchaseItemResultResponse | null;
}

export function PurchaseItemResultProvider({
    children,
    initialIsOpen = false,
    initialPurchaseId = 0,
    initialItemNumber = 0,
    initialResultData = null
}: PurchaseItemResultProviderProps) {
    const [state, dispatch] = useReducer(purchaseItemResultReducer, {
        ...initialState,
        isOpen: initialIsOpen,
        purchaseId: initialPurchaseId,
        itemNumber: initialItemNumber,
        resultData: initialResultData
    });

    React.useEffect(() => {
        dispatch({ type: 'SET_RESULT_DATA', payload: initialResultData });
    }, [initialResultData]);

    React.useEffect(() => {
        dispatch({ type: 'SET_IS_OPEN', payload: initialIsOpen });
    }, [initialIsOpen]);

    return (
        <PurchaseItemResultStateContext.Provider value={state}>
            <PurchaseItemResultDispatchContext.Provider value={dispatch}>
                {children}
            </PurchaseItemResultDispatchContext.Provider>
        </PurchaseItemResultStateContext.Provider>
    );
}

export function usePurchaseItemResultState() {
    const context = useContext(PurchaseItemResultStateContext);
    if (context === undefined) {
        throw new Error('usePurchaseItemResultState must be used within a PurchaseItemResultProvider');
    }
    return context;
}

export function usePurchaseItemResultDispatch() {
    const context = useContext(PurchaseItemResultDispatchContext);
    if (context === undefined) {
        throw new Error('usePurchaseItemResultDispatch must be used within a PurchaseItemResultProvider');
    }
    return context;
}
